import React, { useState, useRef, useEffect } from "react";
import { C, font, MOIS } from "../config/theme";

/* ─── Select dropdown simple ─── */
function FilterSelect({ label, value, onChange, options, allLabel = "Tous" }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
      <label style={{ fontSize: 10, fontWeight: 600, color: C.slate, textTransform: "uppercase" }}>{label}</label>
      <select value={value} onChange={(e) => onChange(e.target.value)} style={{
        padding: "6px 10px", borderRadius: 8, border: `1px solid ${C.border}`,
        fontSize: 12, color: C.text, fontFamily: font, minWidth: 130, background: C.surface,
      }}>
        <option value="">{allLabel}</option>
        {options.map((o) => (
          <option key={o.value || o.id} value={o.value || o.id}>{o.label}</option>
        ))}
      </select>
    </div>
  );
}

/* ─── Autocomplete avec recherche (pour les clients) ─── */
function FilterAutocomplete({ label, value, onChange, options, allLabel = "Tous les clients", placeholder = "Rechercher..." }) {
  const [search, setSearch] = useState("");
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const filtered = search.length >= 1
    ? options.filter((o) => o.label.toLowerCase().includes(search.toLowerCase())).slice(0, 20)
    : options.slice(0, 20);

  const selectedLabel = value ? (options.find((o) => (o.value || o.id) === value)?.label || value) : "";

  return (
    <div ref={ref} style={{ display: "flex", flexDirection: "column", gap: 2, position: "relative" }}>
      <label style={{ fontSize: 10, fontWeight: 600, color: C.slate, textTransform: "uppercase" }}>{label}</label>
      <div style={{ position: "relative" }}>
        <input
          type="text"
          value={open ? search : selectedLabel}
          placeholder={value ? selectedLabel : placeholder}
          onChange={(e) => { setSearch(e.target.value); setOpen(true); }}
          onFocus={() => { setOpen(true); setSearch(""); }}
          style={{
            padding: "6px 28px 6px 10px", borderRadius: 8, border: `1px solid ${open ? C.accent : C.border}`,
            fontSize: 12, color: C.text, fontFamily: font, minWidth: 180, background: C.surface,
            outline: "none", transition: "border 150ms",
          }}
        />
        {value && (
          <div
            onClick={(e) => { e.stopPropagation(); onChange(""); setSearch(""); setOpen(false); }}
            style={{
              position: "absolute", right: 6, top: "50%", transform: "translateY(-50%)",
              cursor: "pointer", color: C.slateLight, fontSize: 14, lineHeight: 1,
              width: 18, height: 18, display: "flex", alignItems: "center", justifyContent: "center",
              borderRadius: "50%", background: C.bg,
            }}
          >
            ×
          </div>
        )}
      </div>

      {open && (
        <div style={{
          position: "absolute", top: "100%", left: 0, right: 0, marginTop: 4,
          background: C.surface, border: `1px solid ${C.border}`, borderRadius: 8,
          boxShadow: "0 4px 16px rgba(0,0,0,0.12)", maxHeight: 240, overflowY: "auto",
          zIndex: 100, fontSize: 12,
        }}>
          <div
            onClick={() => { onChange(""); setSearch(""); setOpen(false); }}
            style={{
              padding: "8px 12px", cursor: "pointer",
              color: !value ? C.accent : C.slate, fontWeight: !value ? 700 : 400,
              background: !value ? `${C.accent}08` : "transparent",
              borderBottom: `1px solid ${C.border}`,
            }}
            onMouseEnter={(e) => e.target.style.background = C.bg}
            onMouseLeave={(e) => e.target.style.background = !value ? `${C.accent}08` : "transparent"}
          >
            {allLabel}
          </div>

          {filtered.length === 0 && (
            <div style={{ padding: "12px", color: C.slateLight, textAlign: "center", fontSize: 11 }}>
              Aucun résultat
            </div>
          )}

          {filtered.map((o) => {
            const optVal = o.value || o.id;
            return (
              <div
                key={optVal}
                onClick={() => { onChange(optVal); setSearch(""); setOpen(false); }}
                style={{
                  padding: "7px 12px", cursor: "pointer",
                  color: value === optVal ? C.accent : C.text,
                  fontWeight: value === optVal ? 600 : 400,
                  background: value === optVal ? `${C.accent}08` : "transparent",
                }}
                onMouseEnter={(e) => e.target.style.background = C.bg}
                onMouseLeave={(e) => e.target.style.background = value === optVal ? `${C.accent}08` : "transparent"}
              >
                {o.label}
              </div>
            );
          })}

          {options.length > 20 && filtered.length >= 20 && (
            <div style={{ padding: "8px 12px", color: C.slateLight, fontSize: 10, textAlign: "center", borderTop: `1px solid ${C.border}` }}>
              Tapez pour filtrer ({options.length} éléments)
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/**
 * FilterBar — Barre de filtres CA (MCD v14)
 *
 * Filtres : Année, Période, Client, Chargé d'affaires, Conducteur, Activité, Agence
 */
export default function FilterBar({
  showAnnee = true, annees = [], annee, setAnnee,
  showPeriode = true, moisDebut, setMoisDebut, moisFin, setMoisFin,
  showMoisUnique = false, mois = null, setMois,
  showClient = true, clients = [], filClient = "", setFilClient,
  showChargeAffaires = true, chargesAffaires = [], filChargeAffaires = "", setFilChargeAffaires,
  showConducteur = true, conducteurs = [], filConducteur = "", setFilConducteur,
  activites = [], filActivite = "", setFilActivite,
  agences = [], filAgence = "", setFilAgence,
}) {
  const nbFiltres = [
    showClient ? filClient : "",
    showChargeAffaires ? filChargeAffaires : "",
    showConducteur ? filConducteur : "",
    showMoisUnique && mois != null ? String(mois) : "",
    filActivite, filAgence,
  ].filter(Boolean).length;

  const resetFiltres = () => {
    if (showClient && setFilClient) setFilClient("");
    if (showChargeAffaires && setFilChargeAffaires) setFilChargeAffaires("");
    if (showConducteur && setFilConducteur) setFilConducteur("");
    if (showMoisUnique && setMois) setMois(null);
    if (setFilActivite) setFilActivite("");
    if (setFilAgence) setFilAgence("");
  };

  return (
    <div style={{
      display: "flex", gap: 12, alignItems: "flex-end", flexWrap: "wrap",
      marginBottom: 24, padding: "16px 20px",
      background: C.surface, borderRadius: 12, border: `1px solid ${C.border}`,
    }}>
      {/* Année */}
      {showAnnee && annees.length > 0 && (
        <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
          <label style={{ fontSize: 10, fontWeight: 600, color: C.slate, textTransform: "uppercase" }}>Année</label>
          <div style={{ display: "flex", gap: 4 }}>
            {annees.map((a) => (
              <button key={a} onClick={() => setAnnee(a)} style={{
                padding: "6px 14px", borderRadius: 8, border: "none",
                fontSize: 13, fontWeight: 700, cursor: "pointer",
                background: annee === a ? C.navy : C.bg, color: annee === a ? "#fff" : C.slate,
              }}>{a}</button>
            ))}
          </div>
        </div>
      )}

      {/* Période */}
      {showPeriode && (
        <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
          <label style={{ fontSize: 10, fontWeight: 600, color: C.slate, textTransform: "uppercase" }}>Période</label>
          <div style={{ display: "flex", gap: 4, alignItems: "center" }}>
            <select value={moisDebut} onChange={(e) => setMoisDebut(+e.target.value)} style={{
              padding: "6px 10px", borderRadius: 8, border: `1px solid ${C.border}`, fontSize: 12, color: C.text, fontFamily: font,
            }}>
              {Array.from({ length: 12 }, (_, i) => i + 1).map((m) => (<option key={m} value={m}>{MOIS[m]}</option>))}
            </select>
            <span style={{ fontSize: 12, color: C.slate }}>→</span>
            <select value={moisFin} onChange={(e) => setMoisFin(+e.target.value)} style={{
              padding: "6px 10px", borderRadius: 8, border: `1px solid ${C.border}`, fontSize: 12, color: C.text, fontFamily: font,
            }}>
              {Array.from({ length: 12 }, (_, i) => i + 1).map((m) => (<option key={m} value={m}>{MOIS[m]}</option>))}
            </select>
          </div>
        </div>
      )}

      {/* Mois unique (alternative à la période) */}
      {showMoisUnique && (
        <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
          <label style={{ fontSize: 10, fontWeight: 600, color: C.slate, textTransform: "uppercase" }}>Mois</label>
          <select
            value={mois ?? ""}
            onChange={(e) => setMois(e.target.value === "" ? null : +e.target.value)}
            style={{
              padding: "6px 10px", borderRadius: 8, border: `1px solid ${C.border}`,
              fontSize: 12, color: C.text, fontFamily: font, minWidth: 110, background: C.surface,
            }}
          >
            <option value="">Toute l'année</option>
            {Array.from({ length: 12 }, (_, i) => i + 1).map((m) => (
              <option key={m} value={m}>{MOIS[m]}</option>
            ))}
          </select>
        </div>
      )}

      {(showAnnee || showPeriode || showMoisUnique) && <div style={{ width: 1, height: 36, background: C.border }} />}

      {/* Client (autocomplete) */}
      {showClient && (
        <FilterAutocomplete label="Client" value={filClient} onChange={setFilClient} options={clients} allLabel="Tous les clients" placeholder="Rechercher un client..." />
      )}

      {/* Chargé d'affaires */}
      {showChargeAffaires && (
        <FilterSelect label="Chargé d'affaires" value={filChargeAffaires} onChange={setFilChargeAffaires} options={chargesAffaires} allLabel="Tous" />
      )}

      {/* Conducteur */}
      {showConducteur && (
        <FilterSelect label="Conducteur" value={filConducteur} onChange={setFilConducteur} options={conducteurs} allLabel="Tous" />
      )}

      {/* Activité */}
      {activites.length > 0 && (
        <FilterSelect label="Activité" value={filActivite} onChange={setFilActivite} options={activites} allLabel="Toutes" />
      )}

      {/* Agence */}
      {agences.length > 0 && (
        <FilterSelect label="Agence" value={filAgence} onChange={setFilAgence} options={agences} allLabel="Toutes" />
      )}

      {/* Bouton réinitialiser */}
      {nbFiltres > 0 && (
        <button onClick={resetFiltres} style={{
          padding: "6px 12px", borderRadius: 8, border: `1px solid ${C.red}40`,
          background: C.redBg, color: C.red, fontSize: 11, fontWeight: 600, cursor: "pointer",
        }}>
          ✕ Réinitialiser ({nbFiltres})
        </button>
      )}
    </div>
  );
}